import React from 'react'

import { Box, useTheme, type SxProps, type Theme } from '@mui/material'
import { ShoppingBag } from 'lucide-react'

export interface ProductImagePlaceholderProps {
  /**
   * Glyph edge in px. Defaults to a size that suits a ~48px table cell; pass a
   * larger value for a hero plate.
   *
   * Absolute px, not a spacing factor — a layer-1 component renders under either
   * app's theme and they use DIFFERENT spacing bases (`@repo/ui` 4px,
   * `apps/app` MUI's default 8px), so any factor here would render at double
   * size in one of them (§47).
   */
  glyphSize?: number
  sx?: SxProps<Theme>
}

/**
 * What fills an image slot when there is NO image.
 *
 * ## One component, both apps, on purpose
 *
 * The empty state was being drawn independently everywhere it appeared — the
 * platform's `Image` fallback, and a hand-rolled grey `Box` at each `apps/app`
 * call site (`CertificatesTeaser`, `PendingSaleTeaser`, the pending-sale page).
 * They disagreed on the grey and none of them carried a glyph, so a table of
 * un-photographed products read as a column of blank slabs.
 *
 * ## The plate is the AVATAR fill, written as the same expression
 *
 * `primary[100] ?? grey[100]` — character-for-character what `@repo/ui`'s
 * `Avatar` uses for a person with no photo. Copied as an EXPRESSION rather than
 * as a resolved hex so the two cannot drift: change Avatar's fill and this
 * follows.
 *
 * It arrived here by elimination. `grey[200]` (82% lightness) read as a solid
 * slab; `grey[50]` (96%) was still heavy; no fill at all left the glyph floating
 * with nothing to sit in. The avatar fill is the answer the app had already
 * reached for the identical problem — a slot whose content is missing — so this
 * is not a fourth opinion, it is the existing one.
 *
 * ⚠️ `CertificatesTeaser` paints `grey[50]` on the image panel even when there IS
 * a photo, so the picture has something to fade up over, and its comment asks the
 * empty state to match. That is now DELIBERATELY not honoured: a loading card and
 * an empty one are different states and reading differently is correct. What was
 * wrong before was the accident — at `grey[100]` an empty card sat darker than a
 * loading one with no reason behind it.
 *
 * ## Why a shopping bag
 *
 * It is this product's OWN glyph for "product" — the platform sidebar's Products
 * entry uses `ShoppingBagIcon`, so a reader has already met it in that meaning.
 * A generic photo/mountain icon (what `Image` drew before) says "an image is
 * missing", which is true but useless; the bag says what the missing thing is
 * OF, which is what a reader scanning a product list actually needs.
 *
 * The glyph sits at `grey[400]` — quiet against a white surface, legible.
 * `strokeWidth: 1` matches the app's icon weight everywhere else.
 */
export const ProductImagePlaceholder = ({
  glyphSize = 20,
  sx,
}: ProductImagePlaceholderProps) => {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'

  return (
    <Box
      aria-hidden
      sx={[
        {
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: isDark
            ? '#27272a'
            : theme.palette.primary?.[100] ?? theme.palette.grey[100],
          color: isDark ? theme.palette.grey[600] : theme.palette.grey[400],
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ] as SxProps<Theme>}
    >
      <ShoppingBag size={glyphSize} strokeWidth={1} />
    </Box>
  )
}

export default ProductImagePlaceholder
